"use client";

import { Search, X } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  filterTopicItems,
  type TopicItem,
  type TopicItemKind,
} from "./topic-items";

const DEFAULT_INITIAL_VISIBLE = 12;

export interface SearchableTopicPillItem extends TopicItem {
  selected: boolean;
  onSelect: () => void;
}

interface SearchableTopicPillsProps {
  emptyState: string;
  footer?: ReactNode;
  initialVisible?: number;
  items: SearchableTopicPillItem[];
  searchPlaceholder?: string;
}

function buildEmptySearchMessage({
  query,
}: {
  query: string;
}): string {
  return `No tags or tools match "${query}"`;
}

function getButtonKindLabel({ kind }: { kind: TopicItemKind }): string {
  return kind === "tool" ? "tool" : "tag";
}

export function SearchableTopicPills({
  emptyState,
  footer,
  initialVisible = DEFAULT_INITIAL_VISIBLE,
  items,
  searchPlaceholder = "Search tags and tools...",
}: SearchableTopicPillsProps) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);

  const filteredItems = useMemo(
    () => filterTopicItems({ items, query }),
    [items, query]
  );

  const hasSearch = query.trim().length > 0;
  const visibleItems = useMemo(() => {
    if (hasSearch || expanded) {
      return filteredItems;
    }
    // Always surface selected (URL-active) items even if they fall past the
    // initialVisible cutoff, so the sidebar reflects the current URL state.
    const selected = filteredItems.filter((item) => item.selected);
    const unselected = filteredItems.filter((item) => !item.selected);
    const unselectedSlots = Math.max(0, initialVisible - selected.length);
    return [...selected, ...unselected.slice(0, unselectedSlots)];
  }, [expanded, filteredItems, hasSearch, initialVisible]);
  const hasMoreToExpand =
    !(hasSearch || expanded) && visibleItems.length < filteredItems.length;

  return (
    <>
      <div className="relative mb-2">
        <Search className="-translate-y-1/2 absolute top-1/2 left-2.5 h-3.5 w-3.5 text-[var(--explore-text-muted)]" />
        <Input
          className="h-8 border-[var(--explore-border-light)] bg-[var(--explore-tag-bg)] pr-3 pl-8 text-[0.8rem] text-[var(--explore-text-primary)] shadow-none placeholder:text-[var(--explore-text-muted)] focus-visible:border-[var(--explore-accent-teal)] focus-visible:ring-0"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder={searchPlaceholder}
          type="text"
          value={query}
        />
      </div>

      {visibleItems.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {visibleItems.map((item) => (
            <Badge
              asChild
              className={cn(
                "cursor-pointer rounded-sm border-0 px-2.5 py-1 font-medium text-[0.8rem] transition-all",
                item.selected
                  ? "bg-[var(--explore-chip-selected-bg)] text-[var(--explore-chip-selected-text)] hover:bg-[var(--explore-chip-selected-bg)]/90"
                  : "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)] hover:opacity-80"
              )}
              key={`${item.kind}:${item.id}`}
            >
              <button
                aria-label={`${item.selected ? "Remove" : "Add"} ${getButtonKindLabel({ kind: item.kind })} filter ${item.label}`}
                aria-pressed={item.selected}
                onClick={item.onSelect}
                type="button"
              >
                {item.label}
                {item.count !== undefined && (
                  <span className="ml-1 text-[0.68rem] opacity-60">
                    {item.count}
                  </span>
                )}
                {item.selected && (
                  <X className="ml-0.5 h-2.5 w-2.5 shrink-0 opacity-70" />
                )}
              </button>
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-[0.78rem] text-[var(--explore-text-muted)]">
          {hasSearch ? buildEmptySearchMessage({ query }) : emptyState}
        </p>
      )}

      {hasMoreToExpand && (
        <button
          className="mt-3 block border-none bg-transparent p-0 font-medium text-[0.8rem] text-[var(--explore-accent-teal)] transition-colors hover:underline"
          onClick={() => setExpanded(true)}
          type="button"
        >
          + Show more
        </button>
      )}

      {footer ? <div className="mt-3">{footer}</div> : null}
    </>
  );
}
